import React, { useState, useMemo } from "react";
import _ from "lodash";
import { Icon } from "@showitinc/showit-wc";
import DesignItemList from "./DesignItemList";
import DesignItem from "./DesignItem";
import DesignBlockList from "./DesignBlockList";
import styles from "./DesignPageList.module.css";

export const groupPagesByFolder = (pages) =>
  pages.reduce((acc, page) => {
    if (page.pId) {
      (acc[page.pId] ??= []).push(page);
    }
    return acc;
  }, {});

const BlockList = ({ page, selected, onItemClick }) => {
  const blocks = _.get(page, "blocks", []);
  const loading = _.get(page, "loading", false);
  const { id } = page;

  return (
    <DesignBlockList
      key={`${id}-blocks`}
      loading={loading}
      selected={selected}
      blocks={blocks}
      onItemClick={onItemClick}
    />
  );
};

/**
 * Page Renderer Component
 * Renders a page item with its contained blocks
 * @component
 * @param {Object} props - Component props (or below)
 * @param {Object} props.page - The page object containing page metadata
 * @param {Object} props.selected - The currently selected item state
 * @param {Function} props.onItemClick - Callback function when a page item is clicked
 * @param {boolean} props.showBlocks - Whether to show block lists for selected pages
 * @returns {React.ReactElement} A page renderer component with nested blocks
 */
const PageRenderer = ({ page, selected, onItemClick, showBlocks }) => {
  const { id, name } = page;
  const selectedId = _.get(selected, "page_id", false);
  const icons = "glyphicon glyphicon-file";

  return (
    <div key={`${id}-${name}`}>
      <DesignItem
        label={name}
        active={id === selectedId}
        iconClasses={icons}
        onClick={() => onItemClick(page)}
      />
      {showBlocks && id === selectedId && (
        <BlockList page={page} selected={selected} onItemClick={onItemClick} />
      )}
    </div>
  );
};

/**
 * Folder Item Component
 * Renders a collapsible folder with its contained pages in the Design Chooser
 * @component
 * @param {Object} props - Component props (or below)
 * @param {Object} props.folder - The folder object containing folder metadata
 * @param {Array} props.pagesInFolder - Array of page objects that belong to this folder
 * @param {Object} props.selected - The currently selected item state
 * @param {Function} props.onItemClick - Callback function when a page item is clicked
 * @param {boolean} props.showBlocks - Whether to show block lists for selected pages
 * @returns {React.ReactElement} A collapsible folder component with nested pages
 */
const FolderItem = ({ folder, pagesInFolder, selected, onItemClick, showBlocks }) => {
  const { id, name } = folder;
  const [isOpen, setIsOpen] = useState(false);

  const folderHeader = (
    <div
      key={`${id}-${name}-folder`}
      onClick={() => setIsOpen(!isOpen)}
      className={styles["folder-item"]}
    >
      <div className={styles["folder-header"]}>
        <Icon
          type={isOpen ? "folderOpen" : "folderClosed"}
          size="20"
          className={styles["folder-icon"]}
        />
        <span>{name}</span>
      </div>
    </div>
  );

  const folderPages =
    isOpen && pagesInFolder && pagesInFolder.length > 0
      ? pagesInFolder.map((page) => (
          <PageRenderer
            key={`${page.id}-${page.name}`}
            page={page}
            selected={selected}
            onItemClick={onItemClick}
            showBlocks={showBlocks}
          />
        ))
      : [];

  return (
    <div className={styles["folder-container"]}>
      <div className={styles["folder-content"]}>
        {folderHeader}
        {folderPages}
      </div>
    </div>
  );
};

/**
 * Design Page List Component
 * Renders a list of pages with their contained blocks
 * @component
 * @param {Object} props - Component props (or below)
 * @param {Array} props.pages - The array of page objects to render
 * @param {Object} props.selected - The currently selected item state
 * @param {string} props.label - The label to display for the page list
 * @param {Function} props.onItemClick - Callback function when a page item is clicked
 * @param {boolean} props.showBlocks - Whether to show block lists for selected pages
 * @returns {React.ReactElement} A design page list component with nested pages and blocks
 */
const DesignPageList = ({ pages, selected, label, onItemClick, showBlocks }) => {
  const _onItemClick = (page) => {
    onItemClick(page);
  };

  const pagesByFolder = useMemo(() => groupPagesByFolder(pages), [pages]);

  const items = pages
    .map((page) => {
      const { id, name, type } = page;

      if (type === "folder") {
        const pagesInFolder = pagesByFolder[id] || [];
        return (
          <FolderItem
            key={`${id}-${name}-folder`}
            folder={page}
            pagesInFolder={pagesInFolder}
            selected={selected}
            onItemClick={_onItemClick}
            showBlocks={showBlocks}
          />
        );
      } else if (!page.pId) {
        return (
          <PageRenderer
            key={`${id}-${name}`}
            page={page}
            selected={selected}
            onItemClick={_onItemClick}
            showBlocks={showBlocks}
          />
        );
      }

      return null;
    })
    .filter(Boolean);

  return <DesignItemList label={label}>{items}</DesignItemList>;
};

export default DesignPageList;
